import { useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { AuthLayout } from "../components/AuthLayout";
import {
  clearSignupAttempt,
  getSignupAttempt,
} from "./MerchantSignup";

/**
 * /merchant/signup/verify — Phase 28 step 2 of 3 (confirm OTP).
 *
 * The attempt token sits in sessionStorage from MerchantSignup.tsx; if
 * a merchant lands here with no stash they bounce back to step 1. On
 * a successful verify we navigate to /merchant/signup/complete which
 * fires the create-tenant call automatically.
 *
 * Cancel hits the public /cancel endpoint best-effort and clears the
 * stash regardless — the server-side cancel is idempotent so a
 * network failure here doesn't leave the merchant stuck.
 */
export function MerchantSignupVerifyPage() {
  const [, navigate] = useLocation();
  const stash = getSignupAttempt();
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);

  if (!stash) {
    return <Redirect to="/merchant/signup" />;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!stash) return;
    if (!/^\d{6}$/u.test(otp)) {
      setErr("Enter the 6-digit code from your text message.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/v1/public/merchant-signup/verify-otp", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          attemptToken: stash.attemptToken,
          otp,
        }),
      });
      const json = (await res.json().catch(() => null)) as
        | {
            data?: { attemptToken: string; step: string };
            error?: {
              code?: string;
              message?: string;
              details?: { attemptsRemaining?: number };
            };
          }
        | null;
      if (!res.ok || !json?.data) {
        const code = json?.error?.code ?? "ERROR";
        if (code === "INVALID_OTP" && typeof json?.error?.details?.attemptsRemaining === "number") {
          setAttemptsRemaining(json.error.details.attemptsRemaining);
        }
        if (code === "OTP_BURNED" || code === "OTP_EXPIRED" || code === "ATTEMPT_EXPIRED") {
          // Hard-stop states — clear the stash and bounce back to
          // step 1 so the merchant restarts cleanly.
          clearSignupAttempt();
          setErr(
            json?.error?.message ??
              "That code is no longer valid. Please start over.",
          );
          setTimeout(() => navigate("/merchant/signup"), 2000);
          return;
        }
        setErr(json?.error?.message ?? "Could not verify code.");
        return;
      }
      navigate("/merchant/signup/complete");
    } catch {
      setErr("Network error. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  async function onCancel() {
    if (!stash) return;
    setBusy(true);
    try {
      await fetch("/api/v1/public/merchant-signup/cancel", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ attemptToken: stash.attemptToken }),
      });
    } catch {
      /* idempotent server-side; best-effort here */
    } finally {
      clearSignupAttempt();
      navigate("/for-merchants");
    }
  }

  return (
    <AuthLayout
      title="Enter your code"
      subtitle={`We sent a 6-digit code to ${stash.ownerPhoneMasked}.`}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="signup-otp" className="label">
            Verification code
          </label>
          <input
            id="signup-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D+/gu, "").slice(0, 6))}
            placeholder="123456"
            className="input font-mono text-center text-2xl tracking-widest"
            data-testid="input-signup-otp"
            maxLength={6}
          />
          {attemptsRemaining !== null && attemptsRemaining > 0 && (
            <p
              className="mt-1 text-xs text-amber-700"
              data-testid="text-signup-otp-attempts-remaining"
            >
              {attemptsRemaining} attempt{attemptsRemaining === 1 ? "" : "s"} left
              before you'll need to start over.
            </p>
          )}
        </div>
        {err && (
          <p
            className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700"
            data-testid="text-signup-verify-error"
          >
            {err}
          </p>
        )}
        <button
          type="submit"
          disabled={busy}
          className="btn-primary w-full"
          data-testid="button-signup-verify-otp"
        >
          {busy ? "Verifying…" : "Verify and continue"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="btn-secondary w-full"
          data-testid="button-signup-cancel"
        >
          Cancel signup
        </button>
        <Link
          href="/merchant/signup"
          className="block text-center text-sm text-navy underline"
          data-testid="link-signup-edit-details"
        >
          Edit my details
        </Link>
      </form>
    </AuthLayout>
  );
}
